'use client'

import { ChevronDown, ChevronUp } from 'lucide-react'
import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { getRenderedScale } from '@/lib/utils/css-zoom'

interface OverlayScrollYProps {
  className?: string
  /** Classes for the scrolling element itself (height cap, flex layout). */
  scrollerClassName?: string
  /** Exposes the scrolling element for measurements and scroll chaining. */
  scrollerRef?: RefObject<HTMLDivElement | null>
  children: ReactNode
  /**
   * Chevron rows above/below the list while there's more that way (like Select's), which
   * scroll it while hovered — for short menus where a hidden overflow is easy to miss.
   */
  scrollButtons?: boolean
  /**
   * Scroll from wheel and touch itself. For a list portaled outside a modal Dialog (a
   * popover in <body>): the Dialog's scroll lock cancels those events everywhere outside
   * its own content, so the browser never scrolls the list natively.
   */
  ownScroll?: boolean
}

/**
 * A vertical scroller whose native scrollbar is hidden (so it takes no width) and replaced by
 * a thin overlay thumb along the right edge, shown while hovered and draggable — the vertical
 * counterpart of OverlayScrollX.
 */
export function OverlayScrollY({
  className,
  scrollerClassName,
  scrollerRef: externalScrollerRef,
  children,
  scrollButtons = false,
  ownScroll = false,
}: OverlayScrollYProps) {
  const internalScrollerRef = useRef<HTMLDivElement>(null)
  const scrollerRef = externalScrollerRef ?? internalScrollerRef
  useOwnScroll(scrollerRef, ownScroll)
  const { canScrollUp, canScrollDown } = useScrollEdges(scrollerRef, scrollButtons)
  return (
    <div className={cn('group/scroll-y relative min-h-0', className)}>
      <div ref={scrollerRef} className={cn('scrollbar-none overflow-y-auto', scrollerClassName)}>
        {children}
      </div>
      {scrollButtons ? (
        <>
          <ScrollButton scroller={scrollerRef} direction="up" active={canScrollUp} />
          <ScrollButton scroller={scrollerRef} direction="down" active={canScrollDown} />
        </>
      ) : null}
      <OverlayThumbY scroller={scrollerRef} />
    </div>
  )
}

// Wheel deltas in lines (Firefox) or pages, to pixels.
const WHEEL_LINE_PX = 16

function useOwnScroll(scrollerRef: RefObject<HTMLElement | null>, enabled: boolean) {
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!enabled || !scroller) return
    // Only claims the gesture while the list can still move that way, so it never traps it.
    const scrollBy = (dy: number) => {
      const max = scroller.scrollHeight - scroller.clientHeight
      const next = Math.min(max, Math.max(0, scroller.scrollTop + dy))
      if (next === scroller.scrollTop) return false
      scroller.scrollTop = next
      return true
    }
    const onWheel = (event: WheelEvent) => {
      const unit =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? WHEEL_LINE_PX
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? scroller.clientHeight
            : 1
      if (scrollBy(event.deltaY * unit)) event.preventDefault()
    }
    let lastTouchY: number | null = null
    const onTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0]?.clientY ?? null
    }
    const onTouchMove = (event: TouchEvent) => {
      const y = event.touches[0]?.clientY
      if (y === undefined || lastTouchY === null) return
      const moved = scrollBy(lastTouchY - y)
      lastTouchY = y
      if (moved && event.cancelable) event.preventDefault()
    }
    scroller.addEventListener('wheel', onWheel, { passive: false })
    scroller.addEventListener('touchstart', onTouchStart, { passive: true })
    scroller.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => {
      scroller.removeEventListener('wheel', onWheel)
      scroller.removeEventListener('touchstart', onTouchStart)
      scroller.removeEventListener('touchmove', onTouchMove)
    }
  }, [scrollerRef, enabled])
}

/** Whether `scroller` has more content above / below its viewport, kept current. */
function useScrollEdges(scrollerRef: RefObject<HTMLElement | null>, enabled: boolean) {
  const [edges, setEdges] = useState({ canScrollUp: false, canScrollDown: false })
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!enabled || !scroller) return
    const update = () => {
      const canScrollUp = scroller.scrollTop > 0
      const canScrollDown = scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1
      setEdges((prev) =>
        prev.canScrollUp === canScrollUp && prev.canScrollDown === canScrollDown
          ? prev
          : { canScrollUp, canScrollDown }
      )
    }
    update()
    scroller.addEventListener('scroll', update, { passive: true })
    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(scroller)
    if (scroller.firstElementChild) resizeObserver.observe(scroller.firstElementChild)
    return () => {
      scroller.removeEventListener('scroll', update)
      resizeObserver.disconnect()
    }
  }, [scrollerRef, enabled])
  return edges
}

// Select's cadence: a small step every 50ms while the pointer rests on the chevron.
const SCROLL_BUTTON_STEP_PX = 24
const SCROLL_BUTTON_INTERVAL_MS = 50

function ScrollButton({
  scroller,
  direction,
  active,
}: {
  scroller: RefObject<HTMLElement | null>
  direction: 'up' | 'down'
  /** False at that edge (or with nothing to scroll): faded out and inert. */
  active: boolean
}) {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const stop = () => {
    if (timerRef.current !== null) clearInterval(timerRef.current)
    timerRef.current = null
  }
  const start = () => {
    stop()
    const step = direction === 'up' ? -SCROLL_BUTTON_STEP_PX : SCROLL_BUTTON_STEP_PX
    timerRef.current = setInterval(
      () => scroller.current?.scrollBy({ top: step }),
      SCROLL_BUTTON_INTERVAL_MS
    )
  }
  // Reaching the edge mid-hover ends the auto-scroll; so does unmounting.
  useEffect(() => {
    if (!active) stop()
  }, [active])
  useEffect(() => stop, [])
  const Icon = direction === 'up' ? ChevronUp : ChevronDown
  return (
    <div
      aria-hidden
      onPointerEnter={active ? start : undefined}
      onPointerLeave={stop}
      // Floats over the list's edge rather than taking a row: no gap above the first item, and
      // nothing shifts when it appears. The fade is the menu surface, so rows slide under it.
      className={cn(
        'from-popover absolute inset-x-0 z-10 flex h-6 cursor-default items-center justify-center to-transparent transition-opacity duration-150',
        direction === 'up' ? 'top-0 bg-linear-to-b' : 'bottom-0 bg-linear-to-t',
        !active && 'pointer-events-none opacity-0'
      )}
    >
      <Icon className="text-foreground/40 size-4" />
    </div>
  )
}

interface OverlayThumbYProps {
  /** The vertically scrolling element: a ref, or the element itself once mounted. */
  scroller: RefObject<HTMLElement | null> | HTMLElement | null
}

/**
 * A thin, draggable vertical thumb drawn over the right edge of `scroller`, shown while an
 * ancestor `group/scroll-y` is hovered. Render it as an absolutely positioned sibling of the
 * scroller inside that (positioned) ancestor; the scroller hides its own vertical bar.
 */
export function OverlayThumbY({ scroller: scrollerProp }: OverlayThumbYProps) {
  const thumbRef = useRef<HTMLDivElement>(null)
  const resolveScroller = () =>
    scrollerProp && 'current' in scrollerProp ? scrollerProp.current : scrollerProp

  useEffect(() => {
    const scroller = resolveScroller()
    const thumb = thumbRef.current
    if (!scroller || !thumb) return

    // Written straight to the DOM: scroll fires every frame, and nothing else needs to know.
    const syncThumb = () => {
      const { scrollHeight, clientHeight, scrollTop } = scroller
      if (scrollHeight <= clientHeight + 1) {
        thumb.style.display = 'none'
        return
      }
      const height = Math.max(24, (clientHeight / scrollHeight) * clientHeight)
      const maxTop = clientHeight - height
      const top = (scrollTop / (scrollHeight - clientHeight)) * maxTop
      thumb.style.display = ''
      thumb.style.height = `${height}px`
      thumb.style.transform = `translateY(${scroller.offsetTop + top}px)`
    }

    syncThumb()
    // Virtualized lists grow their content with no resize on the scroller itself — re-measure
    // on hover too, which is the only time the thumb shows.
    const wrapper = scroller.parentElement
    wrapper?.addEventListener('pointerenter', syncThumb)
    scroller.addEventListener('scroll', syncThumb, { passive: true })
    const resizeObserver = new ResizeObserver(syncThumb)
    resizeObserver.observe(scroller)
    if (scroller.firstElementChild) resizeObserver.observe(scroller.firstElementChild)
    return () => {
      wrapper?.removeEventListener('pointerenter', syncThumb)
      scroller.removeEventListener('scroll', syncThumb)
      resizeObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollerProp])

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const scroller = resolveScroller()
    if (!scroller) return
    // Keep the press off drag handles and row click targets underneath.
    event.stopPropagation()
    event.preventDefault()
    const thumb = event.currentTarget
    thumb.setPointerCapture(event.pointerId)
    const startY = event.clientY
    const startScroll = scroller.scrollTop
    // Thumb px → content px: the scrollable distance over the thumb's travel (its own,
    // possibly min-clamped, height counts).
    const thumbTravel = scroller.clientHeight - thumb.offsetHeight
    const ratio =
      thumbTravel > 0 ? (scroller.scrollHeight - scroller.clientHeight) / thumbTravel : 0
    // Pointer deltas are in viewport px; the thumb and scrollTop are in local px (CSS zoom).
    const scale = getRenderedScale(scroller)
    const onMove = (move: PointerEvent) => {
      scroller.scrollTop = startScroll + ((move.clientY - startY) / scale) * ratio
    }
    const onUp = () => {
      thumb.removeEventListener('pointermove', onMove)
      thumb.removeEventListener('pointerup', onUp)
      thumb.removeEventListener('pointercancel', onUp)
    }
    thumb.addEventListener('pointermove', onMove)
    thumb.addEventListener('pointerup', onUp)
    thumb.addEventListener('pointercancel', onUp)
  }

  return (
    <div
      ref={thumbRef}
      aria-hidden
      onPointerDown={handleThumbPointerDown}
      className="no-drag bg-foreground/25 hover:bg-foreground/40 absolute top-0 right-0 z-20 w-1 cursor-pointer rounded-full opacity-0 transition-opacity duration-150 group-hover/scroll-y:opacity-100"
      style={{ display: 'none' }}
    />
  )
}
